import { client as apollo } from "@/graphql/vue-apollo";
import { unwrapNodes } from "@/helpers/graphql";
import cloneDeep from "lodash-es/cloneDeep";
import isEmpty from "lodash-es/isEmpty";
import isNull from "lodash-es/isNull";
import * as Sentry from "@sentry/vue";
import { businessLogic } from "@/config";

import addToCartMutation from "@/graphql/mutations/AddToCart.gql";
import removeFromCartMutation from "@/graphql/mutations/RemoveFromCart.gql";
import updateItemInCartMutation from "@/graphql/mutations/UpdateInCart.gql";
import cartQuery from "@/graphql/queries/Cart.gql";
import createCartMutation from "@/graphql/mutations/CreateCart.gql";

import { addToCartEvent } from "@/helpers/events";

const state = {
  cart: null,
  isCartOpen: false,
  multipassCheckoutUrl: null,
  cartWarnings: "",
};

const mutations = {
  setCartState(state, value) {
    state.cart = value;
  },
  setCartOpenState(state, value) {
    state.isCartOpen = value;
  },
  setMultipassCheckoutUrl(state, value) {
    state.multipassCheckoutUrl = value;
  },
  setCartWarnings(state, value) {
    state.cartWarnings = value;
  },
};

const getters = {
  cartId: (state) => {
    return state.cart?.id || null;
  },
  lines: (state) => {
    const lines = unwrapNodes(state.cart?.lines?.edges) || [];
    return cloneDeep(lines);
  },
  subtotal: (state) => {
    return state.cart?.estimatedCost?.subtotalAmount || null;
  },
  lineItemsCount: (_, getters) => {
    return getters.lines.length || 0;
  },
};

const actions = {
  setCartWarnings({ commit }, value) {
    commit("setCartWarnings", value);
  },
  async checkCartRules({ commit, getters }, { productId, sellingPlanId }) {
    const { lines } = getters;

    const showCartWarning = (message) => {
      commit("setCartWarnings", message);
      return false;
    };

    // Helper to calculate total cart quantity
    const getTotalQuantity = () =>
      lines.reduce((total, { quantity }) => total + quantity, 0);

    // Helper to calculate product-specific quantities
    const getQuantityByProduct = () => {
      return lines.reduce(
        (
          acc,
          {
            merchandise: {
              product: { id },
            },
            quantity,
          }
        ) => {
          const product = acc.find((item) => item.id === id);
          if (product) {
            product.quantity += quantity;
          } else {
            acc.push({ id, quantity });
          }
          return acc;
        },
        []
      );
    };

    // Check if cart exceeds total quantity allowed
    if (!isNull(businessLogic.maxCartTotalQuantity)) {
      const totalQuantity = getTotalQuantity();
      if (totalQuantity >= businessLogic.maxCartTotalQuantity) {
        return showCartWarning(
          "You have reached the maximum quantity of products allowed in the cart. Your latest item was not added."
        );
      }
    }

    // Check if cart exceeds product-specific quantity allowed
    if (!isNull(businessLogic.maxCartProductQuantity)) {
      const productQuantities = getQuantityByProduct();
      const product = productQuantities.find((item) => item.id === productId);

      if (product?.quantity >= businessLogic.maxCartProductQuantity) {
        return showCartWarning(
          "You have reached the maximum quantity allowed of this product in the cart. Your latest item was not added."
        );
      }
    }

    // Check if mixing items with and without selling plans is allowed
    if (businessLogic.preventSplitCart && lines.length > 0) {
      const hasMixedSellingPlans = () => {
        const allHaveSellingPlan = lines.every(
          (line) => line.sellingPlanAllocation
        );
        const noneHaveSellingPlan = lines.every(
          (line) => !line.sellingPlanAllocation
        );
        return !(allHaveSellingPlan || noneHaveSellingPlan);
      };

      if (hasMixedSellingPlans()) {
        return showCartWarning(
          "You cannot mix items with and without preorders in the same cart. Please checkout separately."
        );
      }

      const allHaveSellingPlanAllocation = lines.every(
        (line) => line.sellingPlanAllocation
      );

      if (sellingPlanId && !allHaveSellingPlanAllocation) {
        return showCartWarning(
          "You cannot mix items with and without preorders in the same cart. Please checkout separately."
        );
      }

      if (!sellingPlanId && allHaveSellingPlanAllocation) {
        return showCartWarning(
          "You cannot mix items with and without preorders in the same cart. Please checkout separately."
        );
      }
    }

    commit("setCartWarnings", "");
    return true;
  },
  async addCartItem(
    { commit, dispatch, state, rootState },
    { productId, variantId, quantity, sellingPlanId, attributes = [] }
  ) {
    const canAdd = await dispatch("checkCartRules", {
      productId,
      sellingPlanId,
    });

    if (!canAdd) {
      return;
    }

    let es_id = "-1";
    try {
      // eslint-disable-next-line no-undef
      es_id = EasySizeParametersDebug.easysize.pageview_id.toString();
    } catch (e) {
      console.log("EasySizeParametersDebug: " + e);
    }

    // We retry adding the cart item if when a user tries adding an item to the cart, the mutation fails.
    // EG where the cart no longer exists on the backend for whatever reason, the mutation would return an error.
    // The catch creates the cart, and then the while loop trys adding the item to cart
    // We limit the maxTries to 2 so we don't get an infinite loop.
    let count = 0;
    const maxTries = 2;
    const newAttributes = [...attributes, { key: "_es_id", value: es_id }];

    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const { data } = await apollo.mutate({
          mutation: addToCartMutation,
          variables: {
            cartId: state.cart.id,
            lines: [
              {
                quantity,
                merchandiseId: variantId,
                attributes: newAttributes,
                sellingPlanId,
              },
            ],
          },
        });

        commit("setCartState", data.cartLinesAdd.cart);

        try {
          addToCartEvent({
            sku: variantId,
            quantity: quantity,
            cart: data.cartLinesAdd.cart,
          });
        } catch (err) {
          console.log(err);
        }

        try {
          const cartLines = unwrapNodes(data.cartLinesAdd.cart.lines?.edges);
          if (!cartLines) {
            return;
          }

          const productId = cartLines[0].merchandise.product.id;
          if (!rootState.product.wishlist.includes(productId)) {
            return;
          }

          dispatch("product/removeFromWishList", productId, {
            root: true,
          });
        } catch (err) {
          console.log(err);
        }

        return;
      } catch (error) {
        if (++count === maxTries) {
          console.error("Cart: Item couldn't be added to cart", error);
        }
        console.warn("Cart: Cart could not be fetched, creating new cart");
        await dispatch("createCart");
      }
    }
  },
  async removeCartItem({ commit, state }, cardLineId) {
    const cartId = state.cart.id;

    try {
      const { data } = await apollo.mutate({
        mutation: removeFromCartMutation,
        variables: {
          cartId,
          lineIds: [cardLineId],
        },
      });

      commit("setCartState", data.cartLinesRemove.cart);
    } catch (error) {
      console.log(error);
    }
  },
  async updateCartItem({ state, commit }, line) {
    const cartId = state.cart.id;

    try {
      const { data } = await apollo.mutate({
        mutation: updateItemInCartMutation,
        variables: {
          cartId,
          lines: [line],
        },
      });

      commit("setCartState", data.cartLinesUpdate.cart);
    } catch (error) {
      console.log(error);
    }
  },
  async createCart({ commit, dispatch }) {
    try {
      const { data } = await apollo.mutate({
        mutation: createCartMutation,
      });

      localStorage.setItem("shopifyCartId", data.cartCreate.cart.id);
      commit("setCartState", data.cartCreate.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.log(error);
    }
  },
  async fetchCart({ commit, dispatch }) {
    const localStorageCartId = localStorage.getItem("shopifyCartId");

    if (!localStorageCartId) {
      console.log("Cart: Cart ID not set in location storage");
      dispatch("createCart");
      return;
    }

    try {
      const { data } = await apollo.query({
        query: cartQuery,
        variables: {
          id: localStorageCartId,
        },
      });

      if (!data.cart) {
        console.warn("Cart: Cart null, creating new cart");
        dispatch("createCart");
        return;
      }

      commit("setCartState", data.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.warn("Cart: Cart could not be fetched, creating new cart");
      dispatch("createCart");
    }
  },
  setCartOpen({ commit }, value) {
    commit("setCartOpenState", value);
  },
  multipassCheckoutUrl: {
    root: true,
    async handler({ commit, state, rootState }) {
      const email = rootState.app.customerEmail;

      if (!email) {
        return false;
      }

      const checkoutUrl = state.cart.checkoutUrl;
      const domain = new URL(checkoutUrl);
      const pathQuery = domain.pathname + domain.search;

      const input = {
        email: rootState.app.customerEmail,
        redirectUrl: pathQuery,
      };

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassCheckoutUrl input " + JSON.stringify(input),
        level: "info",
      });

      const multipassResponse = await fetch(
        process.env.RATANDBOA_NODE_APP_URL +
          "/multipass?" +
          new URLSearchParams(input)
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message:
          "multipassAccount response " + JSON.stringify(multipassResponse),
        level: "info",
      });

      const multipassData = await multipassResponse.json();
      if (isEmpty(multipassData)) {
        Sentry.captureMessage(
          "Empty multipassData cart: " + JSON.stringify(multipassData)
        );
        return;
      }

      commit("setMultipassCheckoutUrl", multipassData.url);
    },
  },
  applyDiscount() {
    const urlParams = new URLSearchParams(window.location.search);
    const discountCode = urlParams.get("discount");
    if (discountCode) {
      document.cookie = `discount_code=${discountCode}; path=/`;
      fetch(`/discount/${discountCode}`).catch(function (error) {
        console.error("Error:", error);
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
